import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DENSITY_LIMITS } from '@/entities/consignments';

export type DensityValidationStatus = 'valid' | 'below-air' | 'above-iron';

export interface DensityValidationResult {
  isValid: boolean;
  status: DensityValidationStatus;
  density: number | null;
  errorMessage: string | null;
}

interface UseDensityValidationParams {
  weight?: number;
  volume?: number;
  enabled?: boolean;
}

/**
 * Calculate density from weight and volume
 */
export function calculateDensity(weightKg: number, volumeM3: number): number | null {
  if (volumeM3 <= 0) {
    return null;
  }
  return weightKg / volumeM3;
}

/**
 * Check if density is within valid range
 */
export function isDensityValid(density: number): boolean {
  return density >= DENSITY_LIMITS.MIN && density <= DENSITY_LIMITS.MAX;
}

export function useDensityValidation({
  weight,
  volume,
  enabled = true
}: UseDensityValidationParams): DensityValidationResult {
  const { t } = useTranslation();

  return useMemo(() => {
    // Skip validation if disabled or missing values
    if (!enabled || !weight || !volume || volume <= 0) {
      return {
        isValid: true,
        status: 'valid' as DensityValidationStatus,
        density: null,
        errorMessage: null
      };
    }

    const density = calculateDensity(weight, volume);

    // Check if density is within valid range
    if (density !== null && isDensityValid(density)) {
      return {
        isValid: true,
        status: 'valid' as DensityValidationStatus,
        density,
        errorMessage: null
      };
    }

    // Density is below air density
    if (density !== null && density < DENSITY_LIMITS.MIN) {
      return {
        isValid: false,
        status: 'below-air' as DensityValidationStatus,
        density,
        errorMessage: t('validation.density.belowAir', {
          defaultValue: `Density {{density}} kg/m³ is less than air density ({{minDensity}} kg/m³)`,
          density: density.toFixed(2),
          minDensity: DENSITY_LIMITS.MIN
        })
      };
    }

    // Density exceeds iron density
    return {
      isValid: false,
      status: 'above-iron' as DensityValidationStatus,
      density,
      errorMessage: t('validation.density.aboveIron', {
        defaultValue: `Density {{density}} kg/m³ exceeds iron density ({{maxDensity}} kg/m³)`,
        density: density?.toFixed(2),
        maxDensity: DENSITY_LIMITS.MAX
      })
    };
  }, [weight, volume, enabled, t]);
}
